import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import CategoryFeedback, { CategoryFeedbackValue } from './CategoryFeedback';
import { QcCategoriesState } from './qcCategories';

/** What a reviewer can say about a lesion's shot set. No outcome — only images are marked. */
export type LesionFeedbackValue = CategoryFeedbackValue;

interface LesionPanelProps {
    /** The lesion-level categories for this study, and where that fetch has got to. */
    categories: QcCategoriesState;
    /** The lesion's `body_map_code` answer, already resolved to a label. Null when unanswered. */
    bodyRegion: string | null;
    /** How many photographs hang off this lesion. */
    imageCount: number;
    value: LesionFeedbackValue;
    onChange: (next: LesionFeedbackValue) => void;
    /** The visit is approved (LOCKED). */
    readOnly?: boolean;
}

/**
 * SKIN-241 — feedback about a lesion's shot set rather than about any one photograph.
 *
 * **One panel per lesion.** The handoff draws a single panel per visit; production says
 * that is wrong for roughly a fifth of them (306 visits with one lesion, 78 with two or
 * more, one with 16), and a shared panel would attribute one lesion's missing shot to all
 * of them.
 *
 * It exists because some problems are an **absence**: "the macro shot was never taken"
 * cannot be attached to a photograph that does not exist. Its anchor is the eCRF
 * repeat-group record — `groupRecordUuid` — which is what the API's lesion upsert takes.
 *
 * **There is no outcome.** A lesion is never marked correct or incorrect; approval counts
 * image outcomes only, and lesion feedback never gates it. That asymmetry is deliberate,
 * not an omission: a lesion has no photograph to be right or wrong about.
 */
export default function LesionPanel({
    categories,
    bodyRegion,
    imageCount,
    value,
    onChange,
    readOnly = false,
}: LesionPanelProps) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }} data-testid="lesion-panel">
            <Typography variant="subtitle2">
                <Translate id="hospital.quality-check.review.lesion-feedback" />
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" data-testid="lesion-panel-context">
                {/*
                  * Identifies WHICH lesion this panel governs. On a visit with several,
                  * identical panels stacked down the page would be worse than one: the
                  * reviewer would have no way to tell which shot set they are complaining
                  * about.
                  */}
                {/*
                  * Composed rather than filtered. The `.filter(Boolean).join(' · ')` idiom
                  * belongs where both halves are nullable; a template literal never is, so
                  * the count always survived the filter and rendered as a naked digit on a
                  * lesion whose body region the eCRF left unanswered.
                  */}
                {bodyRegion && `${bodyRegion} · `}
                {imageCount}
                {' '}
                <Translate id="hospital.quality-check.review.image-count" />
            </Typography>

            <Box sx={{ mt: 1 }}>
                <CategoryFeedback
                    categories={categories}
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    // One section, inside a Paper already headed "Lesion feedback" — a
                    // heading here would only repeat it. The image case takes two sections
                    // and needs them told apart.
                    showSectionHeadings={false}
                    // An empty panel on an approved visit is ambiguous: it reads as
                    // feedback that failed to load rather than a reviewer with nothing
                    // to say.
                    emptyReadOnlyMessageId="hospital.quality-check.review.no-lesion-feedback"
                    // Distinct from the image note: this one is about the shot SET, which is
                    // the distinction the whole panel exists to make.
                    notePlaceholderId="hospital.quality-check.review.lesion-note-placeholder"
                />
            </Box>
        </Paper>
    );
}
